import React, { useState, useEffect } from "react";

import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";

import { CheckCircleIcon } from "@heroicons/react/24/outline";

import CustomButton from "./CustomButton";

import Select, { components } from "react-select";

import { animatedComponents, customStyles } from "../utils/constants";

import { UseGetRfiListForFilters } from "../custom-hooks/queries";

export default function JoinAssessmentConfirmationModal({
  open,
  setOpen,
  onConfirm,
  servicesScopes = [], // Provide default empty array
}) {
  const [additionalInfo, setAdditionalInfo] = useState("");

  const { data: rfis } = UseGetRfiListForFilters();

  const optionsForMultiSelect =
    rfis?.map((item) => ({
      value: item?.rfi_id,
      label: item?.rfi_name,
    })) || [];

  // Convert servicesScopes to format needed for react-select
  const formattedServiceScopes =
    servicesScopes?.map((item) => ({
      value: item.rfi_id,
      label: item.rfi_name,
    })) || [];

  // Initialize with empty array, will be populated in useEffect
  const [selectedOptions, setSelectedOptions] = useState([]);

  // Track only the additional selections (not preselected ones)
  const [additionalSelections, setAdditionalSelections] = useState([]);

  // Update selectedOptions whenever servicesScopes changes
  useEffect(() => {
    if (servicesScopes && servicesScopes.length > 0) {
      const formattedScopes = servicesScopes.map((item) => ({
        value: item.rfi_id,
        label: item.rfi_name,
      }));

      setSelectedOptions(formattedScopes);
    }
  }, [servicesScopes]);

  // Also update when modal opens
  useEffect(() => {
    if (open && servicesScopes && servicesScopes.length > 0) {
      const formattedScopes = servicesScopes.map((item) => ({
        value: item.rfi_id,
        label: item.rfi_name,
      }));

      setSelectedOptions(formattedScopes);
      // Reset additional selections when modal opens
      setAdditionalSelections([]);
    }
  }, [open, servicesScopes]);

  const handleJoin = () => {
    // Only send the additional selections in the payload, not the preselected ones
    const additionalServices = additionalSelections.map((option) => ({
      rfi_id: option.value,
      rfi_name: option.label,
    }));

    onConfirm(additionalInfo, additionalServices);
  };

  const handleChange = (selected = []) => {
    // Handle the case where selected might be null
    if (!selected) {
      setSelectedOptions([...formattedServiceScopes]);
      setAdditionalSelections([]);
      return;
    }

    // Keep all servicesScopes items and add any new selections
    const preSelectedValues = servicesScopes.map((item) => item.rfi_id);

    // Extract additional selections (not in preSelectedValues)
    const newSelections = selected.filter(
      (item) => !preSelectedValues.includes(item.value)
    );

    // Store additional selections separately
    setAdditionalSelections(newSelections);

    // Update the full selection display
    setSelectedOptions([...formattedServiceScopes, ...newSelections]);
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      className="relative z-50"
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-opacity-40 backdrop-blur-sm backdrop-filter transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-center shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="flex flex-col items-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>

              <DialogTitle
                as="h3"
                className="mt-3 text-base font-semibold leading-6 text-gray-900"
              >
                Join Assessment Confirmation
              </DialogTitle>

              <p className="mt-2 text-sm text-gray-500">
                Please provide any additional details.
              </p>

              <div className="w-full mt-4">
                <label
                  htmlFor="additionalInfo"
                  className="block text-sm font-medium text-gray-700"
                >
                  Additional Services
                </label>

                <Select
                  closeMenuOnSelect={false}
                  isMulti
                  components={{
                    ...animatedComponents,
                    MultiValueRemove: (props) => {
                      const isPreSelected = servicesScopes.some(
                        (item) => item.rfi_id === props.data.value
                      );
                      if (isPreSelected) {
                        return null;
                      }
                      return <components.MultiValueRemove {...props} />;
                    },
                    MultiValueLabel: (props) => {
                      const isPreSelected = servicesScopes.some(
                        (item) => item.rfi_id === props.data.value
                      );
                      return (
                        <div
                          style={{
                            background: "#f3f4f6", // light gray for preselected
                            padding: "2px 8px",
                            opacity: isPreSelected ? 0.5 : 1,
                            // whiteSpace: "normal",
                            // overflow: "visible",
                            textOverflow: "unset",
                            maxWidth: "300px",
                            // wordBreak: "break-word",
                            borderRadius: "0.375rem",
                            color: "black",
                          }}
                        >
                          {props.data.label}
                        </div>
                      );
                    },
                  }}
                  options={optionsForMultiSelect}
                  value={selectedOptions}
                  onChange={handleChange}
                  isClearable={additionalSelections.length > 0}
                  className="w-full"
                  styles={{
                    ...customStyles,
                    menuList: (base) => ({
                      ...base,
                      maxHeight: "150px",
                      overflowY: "auto",
                    }),
                    multiValue: (base, state) => {
                      const isPreSelected = servicesScopes.some(
                        (item) => item.rfi_id === state.data.value
                      );
                      return {
                        ...base,
                        backgroundColor: "#f3f4f6",
                        opacity: 1,
                        maxWidth: "100%",
                        borderRadius: "0.375rem",
                      };
                    },
                    valueContainer: (base) => ({
                      ...base,
                      flexWrap: "wrap",
                      overflow: "visible",
                      maxHeight: "none",
                    }),
                    option: (base, state) => {
                      const isPreSelected = servicesScopes.some(
                        (item) => item.rfi_id === state.data.value
                      );
                      return {
                        ...base,
                        backgroundColor: isPreSelected
                          ? "#f3f4f6"
                          : state.isSelected
                          ? base.backgroundColor
                          : base.backgroundColor,
                        color: base.color,
                        cursor: isPreSelected ? "not-allowed" : "default",
                        fontStyle: "normal",
                      };
                    },
                  }}
                  isOptionDisabled={(option) =>
                    servicesScopes.some((item) => item.rfi_id === option.value)
                  }
                />
              </div>

              <div className="w-full mt-4">
                <label
                  htmlFor="additionalInfo"
                  className="block text-sm font-medium text-gray-700"
                >
                  Additional Information
                </label>

                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  placeholder="Enter additional information here..."
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                />
              </div>
            </div>

            <div className="mt-5 sm:mt-4 flex justify-center space-x-3">
              <CustomButton
                text="Close"
                width="w-48"
                height="h-10"
                weight="font-semibold"
                size="text-md"
                color="bg-white"
                textColor="text-black"
                borderRadius="rounded-md"
                border="border border-gray-500"
                onClick={() => setOpen(false)}
              />

              <CustomButton
                text="Join"
                width="w-48"
                height="h-10"
                weight="font-semibold"
                size="text-md"
                color="bg-indigo-600"
                textColor="text-white"
                borderRadius="rounded-md"
                border="border border-blue-500"
                onClick={handleJoin}
              />
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
